/**
 * Connector management API client.
 *
 * Manages third-party service connections (QuickBooks, Gmail, etc.)
 * and sync operations. Uses a separate axios instance pointed at the
 * connector_sync_service URL.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type { ConnectedService } from '@/types/dataSource';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Axios instance for connector_sync_service
// ---------------------------------------------------------------------------

const connectorApi = axios.create({
  baseURL: config.CONNECTOR_SYNC_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

connectorApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConnectorResponse {
  id: string;
  source_system: string;
  auth_type: string;
  status: string;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

interface SyncTriggerResponse {
  sync_batch_id: string;
  status: string;
  message: string;
}

interface SyncStatusResponse {
  connector_id: string;
  source_system: string;
  status: string;
  last_sync_at: string | null;
  record_counts: Record<string, number>;
}

interface OAuthAuthorizeResponse {
  authorization_url: string;
  provider: string;
}

// ---------------------------------------------------------------------------
// Provider display metadata
// ---------------------------------------------------------------------------

const PROVIDER_DISPLAY: Record<string, { displayName: string }> = {
  quickbooks: { displayName: 'QuickBooks' },
  gmail: { displayName: 'Gmail' },
  google_drive: { displayName: 'Google Drive' },
  method_crm: { displayName: 'Method CRM' },
  connecteam: { displayName: 'Connecteam' },
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** List all connectors for the current tenant. */
export async function listConnectors(): Promise<ConnectorResponse[]> {
  const { data } = await connectorApi.get('/connectors');
  return data;
}

/** Map backend connector responses to frontend ConnectedService format. */
export function toConnectedServices(connectors: ConnectorResponse[]): ConnectedService[] {
  return connectors.map((c) => ({
    id: c.id,
    provider: c.source_system as ConnectedService['provider'],
    displayName: PROVIDER_DISPLAY[c.source_system]?.displayName ?? c.source_system,
    status: c.status === 'active' ? 'connected' : 'error',
    lastSyncAt: c.last_sync_at ?? undefined,
  }));
}

/** His own send grant (XC-EMAIL-SEND): offered (the workspace's switch on and the sending client set), granted,
 * and the address AskWingo sends his approved vendor orders from. */
export interface GmailSendStatus {
  offered: boolean;
  granted: boolean;
  address: string | null;
}

function gmailSendStatusOf(raw: unknown): GmailSendStatus {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const address = typeof o.address === 'string' && o.address ? o.address : null;
  return { offered: o.offered === true, granted: o.granted === true && address !== null, address };
}

export async function getGmailSendStatus(): Promise<GmailSendStatus> {
  const { data } = await connectorApi.get('/connectors/oauth/gmail_send/status');
  return gmailSendStatusOf(data);
}

/** Stop sending: his send grant revoked (nobody else's). */
export async function revokeGmailSend(): Promise<GmailSendStatus> {
  const { data } = await connectorApi.post('/connectors/oauth/gmail_send/revoke');
  return gmailSendStatusOf(data);
}

/** Get the OAuth authorization URL for a provider (opens in popup). */
export async function getOAuthAuthorizeUrl(provider: string): Promise<string> {
  const { data } = await connectorApi.get<OAuthAuthorizeResponse>(
    `/connectors/oauth/${provider}/authorize`,
  );
  return data.authorization_url;
}

/** Trigger a sync for a connector. Returns 202 immediately. */
export async function triggerSync(connectorId: string): Promise<SyncTriggerResponse> {
  const { data } = await connectorApi.post(`/connectors/${connectorId}/sync`);
  return data;
}

/** Get the sync status for a connector. */
export async function getSyncStatus(connectorId: string): Promise<SyncStatusResponse> {
  const { data } = await connectorApi.get(`/connectors/${connectorId}/sync/status`);
  return data;
}

/** Disconnect (soft-delete) a connector. */
export async function disconnectConnector(connectorId: string): Promise<void> {
  await connectorApi.delete(`/connectors/${connectorId}`);
}

/**
 * Disconnect QuickBooks: revokes token at Intuit, then deletes locally.
 * `realmId` (the confirm step after an Intuit-initiated disconnect) must be
 * this tenant's own QuickBooks company, else the backend refuses with 409.
 */
export async function disconnectQuickBooks(realmId?: string): Promise<void> {
  await connectorApi.post(
    '/connectors/oauth/quickbooks/disconnect',
    null,
    realmId ? { params: { realm_id: realmId } } : undefined,
  );
}

/**
 * Finish an OAuth connection as the signed-in user (CASA 3.2.2). The backend
 * callback forwards `code` + `state` here in the URL fragment; the exchange
 * happens only if the state was issued to this user and has not been used.
 */
export async function completeOAuth(
  provider: string,
  body: { code: string; state: string; realm_id?: string },
): Promise<void> {
  await connectorApi.post(`/connectors/oauth/${encodeURIComponent(provider)}/complete`, body);
}

/** Seed field mappings for a connector from the YAML config. */
export async function seedMappings(connectorId: string): Promise<{ seeded_count: number }> {
  const { data } = await connectorApi.post(`/connectors/${connectorId}/mappings/seed`);
  return data;
}

// ---------------------------------------------------------------------------
// Scheduler config
// ---------------------------------------------------------------------------

interface SchedulerConfigResponse {
  sync_interval_minutes: number;
}

/** Fetch the current scheduler configuration (sync interval). */
export async function getSchedulerConfig(): Promise<SchedulerConfigResponse> {
  const { data } = await connectorApi.get<SchedulerConfigResponse>('/scheduler/config');
  return data;
}
